import { ApolloClient } from '@apollo/client';
import { BaseDataProvider, DataProviderParams, DataProviderResult } from '../base/BaseDataProvider';
import { GET_SUPPLEMENTS, GET_ALL_SUPPLEMENT_ORDERS, GET_SUPPLEMENT_ORDER, GET_ALL_PAYMENT_TRANSACTIONS } from '../queries/supplements.queries';

export class SupplementsDataProvider extends BaseDataProvider {
  async getList(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    this.logInfo(resource, 'getList', params);
    
    try {
      const result = await this.client.query({
        query: GET_SUPPLEMENTS,
        fetchPolicy: 'network-only',
      });

      const data = result.data.supplements || [];
      this.logSuccess(resource, 'getList', { dataLength: data.length });

      return {
        data,
        total: data.length,
      };
    } catch (error: any) {
      this.logError(resource, 'getList', error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    const listResult = await this.getList(resource, params);
    const supplement = listResult.data.find((s: any) => s.id === params.id);
    
    if (!supplement) {
      throw new Error(`Supplement with id ${params.id} not found`);
    }

    return { data: supplement };
  }

  async create(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    console.log('Create not implemented for supplements:', params);
    return { data: { id: Date.now(), ...params.data } };
  }

  async update(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    console.log('Update not implemented for supplements:', params);
    return { data: { id: params.id, ...params.data } };
  }

  async delete(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    console.log('Delete not implemented for supplements:', params);
    return { data: { id: params.id } };
  }
}

export class SupplementOrdersDataProvider extends BaseDataProvider {
  async getList(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    this.logInfo(resource, 'getList', params);
    
    const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
    
    try {
      const result = await this.client.query({
        query: GET_ALL_SUPPLEMENT_ORDERS,
        variables: { limit: perPage * 2, offset: (page - 1) * perPage },
        fetchPolicy: 'network-only',
      });

      const data = result.data.allSupplementOrders || [];
      this.logSuccess(resource, 'getList', { dataLength: data.length });

      return {
        data,
        total: Math.max(data.length, perPage * page),
      };
    } catch (error: any) {
      this.logError(resource, 'getList', error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    this.logInfo(resource, 'getOne', params);

    try {
      const result = await this.client.query({
        query: GET_SUPPLEMENT_ORDER,
        variables: { limit: 1000, offset: 0 },
        fetchPolicy: 'network-only',
      });

      const orders = result.data.allSupplementOrders || [];
      const order = orders.find((ord: any) => ord.id === params.id);
      
      if (!order) {
        throw new Error(`Supplement order with id ${params.id} not found`);
      }

      this.logSuccess(resource, 'getOne', order);
      return { data: order };
    } catch (error: any) {
      this.logError(resource, 'getOne', error, params);
      throw error;
    }
  }

  async create(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    console.log('Create not implemented for supplement orders:', params);
    return { data: { id: Date.now(), ...params.data } };
  }

  async update(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    console.log('Update not implemented for supplement orders:', params);
    return { data: { id: params.id, ...params.data } };
  }

  async delete(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    console.log('Delete not implemented for supplement orders:', params);
    return { data: { id: params.id } };
  }
}

export class PaymentTransactionsDataProvider extends BaseDataProvider {
  async getList(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    this.logInfo(resource, 'getList', params);
    
    const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
    
    try {
      const result = await this.client.query({
        query: GET_ALL_PAYMENT_TRANSACTIONS,
        variables: { limit: perPage * 2, offset: (page - 1) * perPage },
        fetchPolicy: 'network-only',
      });

      const data = result.data.allPaymentTransactions || [];
      this.logSuccess(resource, 'getList', { dataLength: data.length });

      return {
        data,
        total: Math.max(data.length, perPage * page),
      };
    } catch (error: any) {
      this.logError(resource, 'getList', error, params);
      return { data: [], total: 0 };
    }
  }

  async getOne(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    const listResult = await this.getList(resource, params);
    const transaction = listResult.data.find((t: any) => t.id === params.id);
    
    if (!transaction) {
      throw new Error(`Payment transaction with id ${params.id} not found`);
    }

    return { data: transaction };
  }

  async create(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    console.log('Create not implemented for payment transactions:', params);
    return { data: { id: Date.now(), ...params.data } };
  }

  async update(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    console.log('Update not implemented for payment transactions:', params);
    return { data: { id: params.id, ...params.data } };
  }

  async delete(resource: string, params: DataProviderParams): Promise<DataProviderResult> {
    console.log('Delete not implemented for payment transactions:', params);
    return { data: { id: params.id } };
  }
}
